import { useRef, useState } from 'react';
import {
  applyAmountPrecision,
  applyPricePrecision,
  formatReceivedAmount,
  formatReceivedPrice,
  useMediaQuery,
} from '@/shared/lib';
import { AssetBaseInfo } from '@/shared/components';

import type {
  AccountType,
  AssetRests,
  GetAssetsActionButtonsVisibility,
  MarginAssetRests,
  MarginIndicators,
} from '@/entities/account';
import { cn } from '@/shared/utils';
import { HIDDEN_BALANCES_FULL_TEXT } from '@/shared/config';
import { AccountAssetsListItemMenu } from '@/pages/main';

const getRegularAssetValues = (asset: AssetRests, basePrecission: number) => {
  const quantity = applyAmountPrecision(
    asset.available + asset.reserved,
    asset.precision ? asset.precision : basePrecission,
    'credit'
  );
  const roundedQuantityString = formatReceivedAmount(quantity);

  const baseQuantity = applyPricePrecision(
    (asset.baseAvailable ?? 0) + (asset.baseReserved ?? 0),
    basePrecission
  );
  const roundedBaseQuantityString = formatReceivedPrice(baseQuantity);

  return {
    name: asset.name,
    code: asset.code,
    quantity,
    baseQuantity,
    roundedQuantityString,
    roundedBaseQuantityString,
  };
};

const getMarginAssetValues = (
  asset: MarginAssetRests,
  basePrecission: number
) => {
  const roundedQuantityString = formatReceivedAmount(asset.equity ?? 0);

  const baseQuantity = asset.baseEquity ?? 0;
  const roundedBaseQuantityString = formatReceivedPrice(
    applyPricePrecision(baseQuantity, basePrecission)
  );

  return {
    name: asset.name,
    code: asset.code,
    quantity: asset.equity ?? 0,
    baseQuantity,
    roundedQuantityString,
    roundedBaseQuantityString,
  };
};

type Props = {
  isBalancesHidden: boolean;
  accountType: AccountType;
  asset: AssetRests | MarginAssetRests;
  icon: string | undefined;
  basePrecission: number;
  baseAsset: string;
  getButtonsVisibility: GetAssetsActionButtonsVisibility;
  accountNumber: string;
  tradingAccKey?: number;
  openDepositModal?: () => void;
  openWithdrawModal?: () => void;
  openInternalTransferFormModal?: (props?: {
    accountType: AccountType;
    accountNumber: string;
    assetCode: string;
    direction: 'from' | 'to';
  }) => void;
  onExchange: (assetCode: string) => void;
  marginIndicators?: MarginIndicators;
  leverage?: string;
};

const AccountAssetsListItem = ({
  isBalancesHidden,
  accountType,
  asset,
  icon,
  basePrecission,
  baseAsset,
  getButtonsVisibility,
  accountNumber,
  tradingAccKey,
  openDepositModal,
  openWithdrawModal,
  openInternalTransferFormModal,
  onExchange,
  marginIndicators,
  leverage,
}: Props) => {
  const [open, setOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const [isHovered, setIsHovered] = useState(false);
  const justClosedRef = useRef(false);

  const handleOpen = (next: boolean) => {
    if (!next) {
      justClosedRef.current = true;
      requestAnimationFrame(() => {
        justClosedRef.current = false;
      });
    }
    setOpen(next);
    setIsHovered(next);
  };

  const handleMobileClick = () => {
    if (!isMobile || !atLeastOneButtonVisible) return;

    if (justClosedRef.current) {
      justClosedRef.current = false;
      return;
    }

    setOpen(true);
    setIsHovered(true);
  };

  const {
    name,
    code,
    quantity,
    baseQuantity,
    roundedQuantityString,
    roundedBaseQuantityString,
  } =
    accountType === 'leverage' || marginIndicators
      ? getMarginAssetValues(
          marginIndicators
            ? {
                ...asset,
                equity: marginIndicators?.equity,
                baseEquity: marginIndicators?.baseEquity,
                baseEquity00h: marginIndicators?.baseEquity00h,
                margin: marginIndicators?.margin,
                baseMargin: marginIndicators?.baseMargin,
              }
            : (asset as MarginAssetRests),
          basePrecission
        )
      : getRegularAssetValues(asset as AssetRests, basePrecission);

  const displayedRoundedQuantityString = isBalancesHidden
    ? HIDDEN_BALANCES_FULL_TEXT
    : roundedQuantityString;
  const displayedRoundedBaseQuantityString = isBalancesHidden
    ? HIDDEN_BALANCES_FULL_TEXT
    : roundedBaseQuantityString;

  const { deposit, withdraw, exchange, transfer, trade } = getButtonsVisibility(
    accountType,
    code,
    baseQuantity
  );

  const atLeastOneButtonVisible =
    deposit || withdraw || exchange || transfer || trade;

  const showButtons =
    atLeastOneButtonVisible && ((!isMobile && isHovered) || (isMobile && open));

  return (
    <div
      className={cn(
        'flex items-center gap-[0.8rem] py-[1.2rem] h-[6.8rem] rounded-3xl px-[1.6rem]',
        isHovered && 'bg-secondary-bg cursor-pointer'
      )}
      onMouseEnter={() => !isMobile && !open && setIsHovered(true)}
      onMouseLeave={() => !isMobile && !open && setIsHovered(false)}
      onClick={handleMobileClick}>
      <AssetBaseInfo
        asset={{ code, name, icon }}
        leverage={leverage}
        className="grow overflow-hidden"
        nameClassName="truncate"
      />

      <div className="flex flex-col items-end">
        <div className="headline4">{displayedRoundedQuantityString}</div>
        {baseQuantity !== 0 && code != baseAsset && (
          <div className="p5 text-secondary">
            {displayedRoundedBaseQuantityString} {baseAsset}
          </div>
        )}
      </div>

      {showButtons && (
        <AccountAssetsListItemMenu
          available={quantity}
          open={open}
          setOpen={handleOpen}
          onExchange={onExchange}
          openDepositModal={openDepositModal}
          openWithdrawModal={openWithdrawModal}
          openInternalTransferFormModal={openInternalTransferFormModal}
          accountNumber={accountNumber}
          accountType={accountType}
          code={code}
          actionButtonsVisibility={{
            deposit,
            transfer,
            trade,
            withdraw,
            exchange,
          }}
          tradingAccKey={tradingAccKey}
        />
      )}
    </div>
  );
};

export { AccountAssetsListItem };
